import { authApi } from '../auth/api';

export interface AdminUserData {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  isAdmin: boolean;
  interests: string[];
  interestsOther: string | null;
  journeyCount: number;
  journeysGenerated: number;
  friendCount: number;
  loginCount: number;
  lastAccess: string | null;
}

export interface AdminWaitlistEntry {
  id: string;
  email: string;
  firstName: string | null;
  source: string | null;
  interests: string[];
  city: string | null;
  country: string | null;
  acceptedTermsAt: string;
  createdAt: string;
}

export const getAdminUsers = async (): Promise<AdminUserData[]> => {
  const response = await authApi.get('/admin/users');
  return response.data;
};

export const getAdminWaitlistEntries = async (): Promise<AdminWaitlistEntry[]> => {
  const response = await authApi.get('/admin/waitlist');
  return response.data;
};

/** Admin tables with a CSV export (backend/services/adminCsvExport.js). */
export type AdminExportTable = 'users' | 'waitlist' | 'email-logs' | 'llm-requests' | 'affiliate-actions';

/** Rows in the table, and the most one export holds (newest first). */
export const getAdminExportCount = async (table: AdminExportTable): Promise<{ total: number; limit: number }> => {
  const response = await authApi.get(`/admin/export/${table}/count`);
  return response.data;
};

export const downloadAdminExport = async (table: AdminExportTable): Promise<Blob> => {
  const response = await authApi.get(`/admin/export/${table}`, { responseType: 'blob' });
  return response.data;
};

/** The night-train map page's details by name (admins only; the public page has the numbers). */
export interface NightTrainNetworkDetails {
  countries: Array<{ code: string; name: string }>;
  companies: string[];
}

export const getNightTrainNetworkDetails = async (): Promise<NightTrainNetworkDetails> => {
  const response = await authApi.get('/admin/night-trains/network');
  return response.data;
};

export interface AdminTestEmailResult {
  success: boolean;
  messageId: string;
  message: string;
}

export const sendAdminTestEmail = async (to: string): Promise<AdminTestEmailResult> => {
  const response = await authApi.post('/admin/test-email', { to });
  return response.data;
};

export interface AdminEmailLog {
  id: string;
  category: string;
  recipient: string;
  subject: string;
  status: 'sent' | 'failed' | 'suppressed';
  messageId: string | null;
  errorType: string | null;
  userId: string | null;
  createdAt: string;
}

export const getAdminEmailLogs = async (limit = 200): Promise<AdminEmailLog[]> => {
  const response = await authApi.get('/admin/email-logs', { params: { limit } });
  return response.data;
};

export type LlmRequestType = 'journey_generation' | 'leg_pricing' | 'stop_details' | 'journey_edit';

export interface AdminLlmRequest {
  id: string;
  requestType: LlmRequestType | null;
  provider: string;
  model: string | null;
  status: string;
  errorType: string | null;
  tokensUsed: number | null;
  durationMs: number | null;
  iterationNumber: number;
  conversationId: string | null;
  journeyId: string | null;
  requestParams: unknown | null;
  promptSent: string | null;
  responseFull: string | null;
  createdAt: string;
}

// type: pass a LlmRequestType to filter server-side, or omit for all.
export const getAdminLlmRequests = async (
  params: { type?: LlmRequestType; limit?: number } = {}
): Promise<AdminLlmRequest[]> => {
  const response = await authApi.get('/admin/llm-requests', { params });
  return response.data;
};

// --- Omio affiliate conversions (backend: services/affiliateSyncService.js,
// routes /api/admin/affiliate/*; docs/integrations/omio-impact.md "Conversions").

export interface AdminAffiliateAction {
  id: string;
  partner: string;
  campaignId: string | null;
  actionTrackerName: string | null;
  eventCode: string | null;
  state: string;
  amountCents: number | null;
  payoutCents: number | null;
  currency: string | null;
  eventDate: string | null;
  referringDate: string | null;
  lockingDate: string | null;
  clearedDate: string | null;
  journeyId: string | null;
  legId: string | null;
  userId: string | null;
  customerCountry: string | null;
  referringType: string | null;
  firstSeenAt: string;
  lastSyncedAt: string;
}

/** One sync pass's summary (the job's result), or { skipped } when a pass was already running. */
export interface AdminAffiliateSyncResult {
  at?: string;
  reason?: string;
  windows?: number;
  fetched?: number;
  created?: number;
  updated?: number;
  stateChanges?: number;
  durationMs?: number;
  skipped?: boolean;
}

/** A recurring job as the backend registry reports it (backend/jobs/scheduler.js). */
export interface AdminJobStatus<TResult = unknown> {
  key: string;
  name: string;
  enabled: boolean;
  disabledReason: string | null;
  intervalMs: number;
  running: boolean;
  lastRun: { at: string; reason: string; durationMs: number; error: string | null; result: TResult | null } | null;
  nextRunAt: string | null;
  /** No run yet, or the last one older than an interval: the page refreshes on load. */
  stale: boolean;
}

export interface AdminAffiliateSummary {
  configured: boolean;
  /** The affiliate_sync job; null before the registry has it (never in a running server). */
  job: AdminJobStatus<AdminAffiliateSyncResult> | null;
  total: number;
  byState: { state: string; count: number; payoutCents: number; amountCents: number }[];
  byTracker: { actionTrackerName: string | null; count: number; payoutCents: number }[];
  clicks:
    | { since: string; fetchedAt: string; totalClicks: number; months: { month: string | null; clicks: number; actions: number; earnings: number }[] }
    | { error: string }
    | null;
}

export const getAdminAffiliateSummary = async (): Promise<AdminAffiliateSummary> => {
  const response = await authApi.get('/admin/affiliate/summary');
  return response.data;
};

export const getAdminAffiliateActions = async (limit = 200): Promise<AdminAffiliateAction[]> => {
  const response = await authApi.get('/admin/affiliate/actions', { params: { limit } });
  return response.data;
};

// reason 'stale_on_load' = the page's automatic refresh; anything else is 'admin'.
export const runAdminAffiliateSync = async (reason: 'admin' | 'stale_on_load' = 'admin'): Promise<AdminAffiliateSyncResult> => {
  const response = await authApi.post('/admin/affiliate/sync', { reason });
  return response.data;
};

export const getAdminJobs = async (): Promise<AdminJobStatus[]> => {
  const response = await authApi.get('/admin/jobs');
  return response.data;
};

export interface ExtraRecipient {
  email: string;
  firstName?: string | null;
}

export interface AdminCampaignInfo {
  key: string;
  subject: string;
  createdAt: string | null;
  hasCustomAudience: boolean;
}

export interface AdminCampaignStatus {
  campaignKey: string;
  subject: string;
  audience: { total: number; users: number; waitlist: number; extra: number };
  alreadySent: number;
  suppressed: number;
  remaining: number;
  preview: Array<{ email: string; firstName: string | null }>;
  running: boolean;
}

export const listCampaigns = async (): Promise<AdminCampaignInfo[]> => {
  const response = await authApi.get('/admin/campaigns');
  return response.data;
};

export const getCampaignStatus = async (
  key: string,
  extraRecipients: ExtraRecipient[]
): Promise<AdminCampaignStatus> => {
  const response = await authApi.post(`/admin/campaigns/${key}/status`, { extraRecipients });
  return response.data;
};

export const sendCampaignTest = async (key: string, to?: string): Promise<{ success: boolean; messageId: string; to: string }> => {
  const response = await authApi.post(`/admin/campaigns/${key}/test`, to ? { to } : {});
  return response.data;
};

export const startCampaign = async (
  key: string,
  extraRecipients: ExtraRecipient[]
): Promise<AdminCampaignStatus & { started: boolean }> => {
  const response = await authApi.post(`/admin/campaigns/${key}/send`, { confirm: true, extraRecipients });
  return response.data;
};
